import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { legacyC2foAdminUiConfig } from '@c2fo/common-config';
import { Logo } from '@c2fo/liquidity';
import { getUserInitials, getUserEmail, logout } from '../../utils/token';
import { useReporting } from '../../reporting';
import { config } from '../../config';
import { AdminSearchBar } from '../AdminSearchBar';
import { DatacenterDropdown } from '../DatacenterDropdown';
import { ComingSoonNavLink } from '../../feature-flags';
import { PageFlags } from '../../feature-flags/pageFlags';
import type { SearchType } from '../AdminSearchBar';

/** Logout via legacy admin so session is cleared and user is not re-authenticated on new admin login. */
const legacyLogoutUrl = `${legacyC2foAdminUiConfig.LEGACY_C2FO_ADMIN_UI_URL}/logout?redirect-to=${config.NEW_ADMIN_UI_URL}/login`;

export const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { track } = useReporting();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const userInitials = getUserInitials();
  const userEmail = getUserEmail();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDropdownOpen]);

  const handleLogout = () => {
    setIsDropdownOpen(false);
    void track('user-menu::logout::clicked', { fromPage: location.pathname });
    logout(legacyLogoutUrl);
  };

  const handleAdminSearch = (searchType: SearchType, query: string) => {
    const encoded = encodeURIComponent(query);
    switch (searchType) {
      case 'division':
        navigate(`/divisions?q=${encoded}`);
        break;
      case 'user':
        navigate(`/users?q=${encoded}`);
        break;
      case 'market':
        navigate(`/markets?q=${encoded}`);
        break;
    }
  };

  const isActive = (path: string) => {
    if (path === '/users') {
      // Users should be active for /users and /users/:uuid
      return location.pathname === '/users' || location.pathname.startsWith('/users/');
    }

    if (path === '/markets') {
      // Markets should be active for /markets and /markets/:marketUuid
      return location.pathname === '/markets' || location.pathname.startsWith('/markets/');
    }

    // if (path === '/accounts') {
    //   // Accounts should be active for /accounts and /accounts/:accountId
    //   return location.pathname === '/accounts' || location.pathname.startsWith('/accounts/');
    // }

    if (path === '/divisions') {
      // Divisions should be active for /divisions and /divisions/:makerUuid/:divisionId
      return location.pathname === '/divisions' || location.pathname.startsWith('/divisions/');
    }

    if (path === '/insights') {
      return (
        location.pathname === '/insights' ||
        (location.pathname.startsWith('/insights/') && !location.pathname.startsWith('/insights/feedback'))
      );
    }

    return location.pathname === path;
  };

  return (
    <div data-testid="navigation" className="h-12 sticky top-10 z-[9]">
      <nav className="w-full bg-secondary-500 text-white px-4">
        <div className="h-12 flex items-center justify-between max-w-7xl mx-auto gap-6">
          {/* Left side: Logo and Nav Links */}
          <div className="flex items-center gap-8">
            <ComingSoonNavLink
              to="/market-overview"
              splitName={PageFlags.MARKET_OVERVIEW_PAGE}
              className="flex items-center no-underline hover:opacity-80 transition-opacity"
            >
              <Logo color="white" height="32px" />
            </ComingSoonNavLink>

            <div className="flex gap-6 items-center">
              <ComingSoonNavLink
                to="/markets"
                splitName={PageFlags.MARKETS_PAGE}
                className={`text-white font-medium hover:text-gray-200 transition-colors ${
                  isActive('/markets') ? 'border-b-2 border-primary-500' : ''
                }`}
              >
                Markets
              </ComingSoonNavLink>

              <ComingSoonNavLink
                to="/divisions"
                splitName={PageFlags.DIVISIONS_PAGE}
                className={`text-white font-medium hover:text-gray-200 transition-colors ${
                  isActive('/divisions') ? 'border-b-2 border-primary-500' : ''
                }`}
              >
                Divisions
              </ComingSoonNavLink>

              <Link
                to="/users"
                className={`text-white no-underline font-medium hover:text-gray-200 transition-colors ${
                  isActive('/users') ? 'border-b-2 border-primary-500' : ''
                }`}
              >
                Users
              </Link>

              <Link
                to="/eslap"
                className={`text-white no-underline font-medium hover:text-gray-200 transition-colors ${
                  isActive('/eslap') ? 'border-b-2 border-primary-500' : ''
                }`}
              >
                ESLAP
              </Link>

              <ComingSoonNavLink
                to="/award"
                splitName={PageFlags.AWARD_PAGE}
                className={`text-white font-medium hover:text-gray-200 transition-colors ${
                  isActive('/award') ? 'border-b-2 border-primary-500' : ''
                }`}
              >
                Award
              </ComingSoonNavLink>

              <ComingSoonNavLink
                to="/insights"
                splitName={PageFlags.INSIGHTS_PAGE}
                className={`text-white font-medium hover:text-gray-200 transition-colors ${
                  isActive('/insights') ? 'border-b-2 border-primary-500' : ''
                }`}
              >
                ✨ Insights
              </ComingSoonNavLink>
            </div>
          </div>

          {/* Right side: Search, Datacenter Dropdown, and User Avatar */}
          <div className="flex items-center gap-4">
            <AdminSearchBar
              key={
                location.pathname === '/divisions' ||
                location.pathname.startsWith('/divisions/') ||
                location.pathname === '/users' ||
                location.pathname.startsWith('/users/')
                  ? `${location.pathname}${location.search}`
                  : location.key
              }
              onSearch={handleAdminSearch}
            />

            {/* Datacenter Dropdown - feature-gated */}
            <DatacenterDropdown />

            {/* User Avatar with Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                className="flex size-10 items-center justify-center rounded-full bg-purple-500 font-bold text-white shadow-md hover:bg-purple-600 transition-colors shrink-0"
                type="button"
                aria-label="User profile menu"
                aria-expanded={isDropdownOpen}
                aria-haspopup="true"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                data-testid="profile-button"
              >
                {userInitials}
              </button>

              {isDropdownOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                  role="menu"
                  aria-label="Profile menu"
                  data-testid="profile-dropdown"
                >
                  {userEmail && (
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900 truncate">{userEmail}</p>
                    </div>
                  )}
                  <Link
                    to="/admin-user"
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors no-underline"
                    role="menuitem"
                    onClick={() => setIsDropdownOpen(false)}
                    data-testid="user-info-link"
                  >
                    User info
                  </Link>
                  <button
                    type="button"
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                    role="menuitem"
                    onClick={handleLogout}
                    data-testid="logout-button"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </div>
  );
};
